// How one calendar event reads on screen.
//
// Seven kinds, each with its own fields, and this is the one place that turns
// any of them into the three strings a cell or a row shows. Every function here
// is an exhaustive `switch` closed by `assertNever`, so an eighth kind added to
// the contract breaks the build here rather than rendering as a blank chip.
//
// It is a MODULE rather than a component for the usual reason this codebase
// splits them: what a term change says — "35% → 30% recurring, 4 deals" — is a
// decision about what a reader needs, and it is checkable as a string in a way
// it is not as JSX.
//
// The API deliberately sends none of this. §06 has reports return numbers and
// the client decide what they look like, so every phrase below is the SPA's, and
// the tone mapping is here rather than in `format/labels.ts` because `Tone`
// lives in `design/Badge.tsx`, which imports `BadgeColor` from that file — the
// pair would import each other.

import type { CalendarEvent, CalendarEventKind, Currency } from "@crm/shared";
import type { Tone } from "../../design/Badge.js";
import { formatCalendarDate } from "../../format/calendarDate.js";
import {
  cadenceLabel,
  calendarEventKindLabel,
  chargeStatusLabel,
  dealStateLabel,
  implFeeUnresolvedReasonLabel,
  termLineKindLabel,
} from "../../format/labels.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";

/**
 * Seven kinds, seven tones — the whole point of the colour here is telling them
 * apart at a glance in a dense grid, so no two share one. It is never the ONLY
 * cue: every chip carries its kind as text for a screen reader and for a
 * printout, and the filter chips repeat the pairing so the legend is the control.
 */
export function calendarEventKindTone(kind: CalendarEventKind): Tone {
  switch (kind) {
    case "bill_due":
      return "brand";
    case "cost_bill_due":
      return "teal";
    // The two that change what a deal is worth, in the two attention colours:
    // a term change alters every deal under an agreement, a price change one
    // line of one deal.
    case "term_change":
      return "warn";
    case "term_line_change":
      return "info";
    case "impl_fee_due":
      return "good";
    case "deal_start":
      return "slate";
    case "deal_renewal":
      return "neutral";
    default:
      return assertNever(kind, "calendar event kind");
  }
}

/**
 * Where clicking an event goes. A discriminated union rather than a router
 * `to`/`params` pair, so this module states the DESTINATION and the component
 * builds the link — typing TanStack's literal route unions here would drag the
 * route tree into a module that otherwise knows nothing about it.
 *
 * Every event has somewhere to go: a deal where it has one, and the partner
 * otherwise, which is where a term change and a partner-level cost recovery both
 * belong. A calendar is a way IN to a record, not a dead end.
 */
export type CalendarEventTarget =
  | { readonly to: "deal"; readonly id: string }
  | { readonly to: "partner"; readonly id: string };

export function calendarEventTarget(event: CalendarEvent): CalendarEventTarget {
  return event.deal_id === null
    ? { to: "partner", id: event.partner_id }
    : { to: "deal", id: event.deal_id };
}

export interface CalendarEventText {
  /** Who it is about. The line a reader scans down a column of. */
  headline: string;
  /** What happens, in one line. */
  summary: string;
  /**
   * The figure, already formatted, or null for the kinds that have none. The
   * ISO code is omitted: every figure in this report is in the one currency the
   * footnote and the totals state, so repeating it on every chip would cost the
   * width the customer name needs.
   */
  amount: string | null;
}

function money(minor: number | null, currency: Currency): string {
  // A bill may be certain and unpriced. Said in
  // words rather than left blank: an empty amount column reads as "this kind
  // carries no figure", which is true of a term change and is the opposite of
  // what is true here — this row is waiting on a number somebody owes us.
  if (minor === null) return "Pending";
  return formatMoney(minor, currency, { omitCurrencyCode: true });
}

/**
 * A split that moved, as "35% → 30%". Null when it did not move, so a term row
 * lists only what actually changed — an escalator that repriced the recurring
 * share and left the implementation share alone should say so in one clause,
 * not three.
 *
 * A null percentage is rendered "none" rather than 0%: an agreement that sets no
 * cost split is saying cost sharing is not part of the relationship, which is a
 * different statement from splitting it zero/a hundred (see `AgreementTerm`).
 */
function splitMove(label: string, from: number | null, to: number | null): string | null {
  if (from === to) return null;
  const text = (pct: number | null): string => (pct === null ? "none" : formatPercent(pct));
  return `${label} ${text(from)} → ${text(to)}`;
}

/**
 * What a deal's terms come to, or the words for not knowing.
 *
 * A contracted total stops being knowable the moment one active clause is
 * metered: what a metered clause is worth is however many units the facts turn
 * out to carry. Zero is a different claim — a deal with no priced terms at all —
 * and it is drawn as no figure rather than as a figure of nothing.
 */
function contractedAmount(minor: number | null, currency: Currency): string | null {
  if (minor === null) return "Not known";
  return minor === 0 ? null : money(minor, currency);
}

export function describeCalendarEvent(event: CalendarEvent, currency: Currency): CalendarEventText {
  switch (event.kind) {
    case "bill_due": {
      const invoice = event.invoice_number === null ? "" : ` · invoice ${event.invoice_number}`;
      // "Estimate" is stated only while it is one. Once an actual has landed the
      // figure is what was invoiced, and saying "estimate" beside it would be
      // wrong in the direction that matters.
      const basis = event.is_estimate ? " · estimate" : "";
      return {
        headline: event.customer_name,
        summary: `${event.revenue_line_label} · ${chargeStatusLabel(event.status)}${basis}${invoice}`,
        amount: money(event.gross_minor, currency),
      };
    }

    case "cost_bill_due":
      return {
        // The partner is the PAYER on a recharge, but every other kind on this
        // feed headlines the customer, and a row that swapped in the payer for
        // one kind reads as a different customer rather than as a different
        // relationship — "Hansen CIS B" between two Kingsport rows looks like a
        // third account. So the customer leads here too, and the payer moves
        // into the summary beside it.
        //
        // A recharge with no deal genuinely belongs to no customer: it is a cost
        // of the relationship itself. That says "Partner-wide" outright rather
        // than quietly falling back to the partner's name, so the two cases stay
        // distinguishable at a glance.
        headline: event.customer_name ?? "Partner-wide",
        summary: `${event.cost_label} · ${event.partner_name} · ${event.period} · ${chargeStatusLabel(event.status)}`,
        // What goes on the bill — never our share, which we absorb and never
        // invoice. The two are not interchangeable and this is the billable one.
        amount: money(event.partner_share_minor, currency),
      };

    case "term_change": {
      const moves = [
        splitMove("recurring", event.previous_recurring_split_pct, event.recurring_split_pct),
        splitMove("implementation", event.previous_impl_split_pct, event.impl_split_pct),
        splitMove("cost", event.previous_cost_split_pct, event.cost_split_pct),
      ].filter((move): move is string => move !== null);

      const deals = `${formatCount(event.affected_deal_count)} deal${
        event.affected_deal_count === 1 ? "" : "s"
      }`;

      const what = event.is_first_term
        ? `First term · ${formatPercent(event.recurring_split_pct)} recurring, ${formatPercent(event.impl_split_pct)} implementation`
        : moves.length === 0
          ? // A term row that changes no split is legal and worth saying plainly:
            // it usually exists to start a new implementation-fee schedule.
            "New term · same splits"
          : moves.join(", ");

      return { headline: event.agreement_name, summary: `${what} · ${deals}`, amount: null };
    }

    case "impl_fee_due": {
      // Branching on the FIGURE rather than on `unresolved_reason`, though the
      // two always agree on the wire. The event is one flat variant — the
      // resolved/unresolved union lives on the implementation-fee report, which
      // is where the diagnosis belongs — so this is the branch the type system
      // can actually check, and it is the one that decides what gets drawn.
      if (event.suggested_gross_minor === null) {
        return {
          headline: event.customer_name,
          // The reason names the FIRST step to fix, which is the whole value of
          // an unresolved row — it is a worklist entry, not a gap.
          summary: `Cannot be priced — ${
            event.unresolved_reason === null
              ? "the fee schedule produced no price"
              : implFeeUnresolvedReasonLabel(event.unresolved_reason)
          }`,
          amount: null,
        };
      }
      const accounts =
        event.customer_account_count === null
          ? ""
          : ` · ${formatCount(event.customer_account_count)} accounts`;
      // Only when it disagrees with the suggestion, and only as a fact rather
      // than a verdict: the report is the screen that reconciles the two.
      const recorded =
        event.recorded_line_count === 0
          ? " · nothing booked yet"
          : event.recorded_gross_minor === event.suggested_gross_minor
            ? ""
            : ` · ${money(event.recorded_gross_minor, currency)} booked`;
      return {
        headline: event.customer_name,
        summary: `Implementation fee${accounts}${recorded}`,
        amount: money(event.suggested_gross_minor, currency),
      };
    }

    case "term_line_change": {
      // "ends" sits on the LAST day the line applies (the contract's end_date is
      // inclusive), so the wording has to be "through" rather than "until" —
      // "until Sep 30" reads as stopping on the 30th, a month of revenue away.
      const what = event.boundary === "starts" ? "starts" : "runs through";
      const shape = `${termLineKindLabel(event.term_line_kind)} · ${cadenceLabel(event.cadence)}`;
      return {
        headline: event.customer_name,
        summary: `${event.label} ${what} · ${shape}`,
        amount:
          event.amount_minor !== null
            ? money(event.amount_minor, currency)
            : event.rate_minor === null
              ? null
              : `${money(event.rate_minor, currency)}/${event.unit ?? "unit"}`,
      };
    }

    case "deal_start": {
      // A fuzzy start is drawn on its earliest day, so the cell has to say the
      // date is a range or it renders a guess as a commitment (D5).
      const when = event.is_firm
        ? "Deal starts"
        : `Deal starts by ${formatCalendarDate(event.start_date_latest)}`;
      return {
        headline: event.customer_name,
        summary: `${when} · ${dealStateLabel(event.state)}`,
        amount: contractedAmount(event.contracted_gross_minor, currency),
      };
    }

    case "deal_renewal": {
      const term =
        event.contract_term_months === null
          ? ""
          : ` · ${formatCount(event.contract_term_months)}-month term`;
      return {
        headline: event.customer_name,
        summary: `Renewal${term} · ${dealStateLabel(event.state)}`,
        amount: contractedAmount(event.contracted_gross_minor, currency),
      };
    }

    default:
      return assertNever(event, "calendar event");
  }
}

/**
 * The whole event as one sentence, for an `aria-label` and a `title`.
 *
 * Built from the same three strings the cell shows plus the kind and the date,
 * because a chip read out of its column has lost both: "Sunview Utility" alone
 * says nothing about being a bill, or about which day it is on.
 */
export function calendarEventLabel(event: CalendarEvent, currency: Currency): string {
  const text = describeCalendarEvent(event, currency);
  const amount = text.amount === null ? "" : `, ${text.amount}`;
  return `${calendarEventKindLabel(event.kind)} · ${text.headline}${amount} · ${text.summary} · ${formatCalendarDate(event.date)}`;
}
